import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import Heading from '@starbucks-web/pattern-library/lib/components/heading';
import ImageLoader from '@starbucks-web/pattern-library/lib/components/image-loader';
import Card from '@starbucks-web/pattern-library/lib/components/card';

import { configImagesUrlSelector } from 'shared/app/state/selectors/config';
import { universalBenefitCardsSelector } from 'rewards/app/state/selectors/sr6';
import { getCardMetadata } from './card-config';

import messages from './messages';
import styles from './styles.cssm';

const UniversalBenefitBox = () => {
  const imageUrl = useSelector(configImagesUrlSelector);
  const benefitCards = useSelector(universalBenefitCardsSelector);

  const messageResolver = {
    freeMonday: {
      heading: <FormattedMessage {...messages.freeMondayHeading} />,
      body: <FormattedMessage {...messages.freeMondayBody} />,
    },
    personalizedGames: {
      heading: <FormattedMessage {...messages.personalizedGamesHeading} />,
      body: <FormattedMessage {...messages.personalizedGamesBody} />,
    },
    doubleStarPersonalCup: {
      heading: <FormattedMessage {...messages.doubleStarPersonalCupHeading} />,
      body: <FormattedMessage {...messages.doubleStarPersonalCupBody} />,
    },
    coffeeLoop: {
      heading: <FormattedMessage {...messages.coffeeLoopHeading} />,
      body: <FormattedMessage {...messages.coffeeLoopBody} />,
    },
  };

  const getMessageComponents = (messageKey) => {
    return messageResolver[messageKey] || { heading: null, body: null };
  };

  // Transform config cards into renderable card content
  const cardContent = benefitCards
    .map((card) => {
      const metadata = getCardMetadata(card.id);
      const messageComponents = getMessageComponents(metadata.messageKey);

      if (
        !metadata.image ||
        !metadata.messageKey ||
        !messageComponents.heading ||
        !messageComponents.body
      ) {
        return null;
      }

      return {
        id: card.id,
        image: metadata.image,
        heading: messageComponents.heading,
        body: messageComponents.body,
      };
    })
    .filter(Boolean);

  // Fallback if no cards are configured
  if (cardContent.length === 0) {
    return (
      <div
        className={`${styles.universalBenefitBoxContainer} py7 px3 md-px0`}
        data-e2e="universal-benefit-box-fallback"
      >
        <Heading
          className={`${styles.heading} text-center text-bold pb5 color-greenOnPrimary`}
          size="lg"
          tagName="h2"
        >
          <FormattedMessage {...messages.universalBenefitBoxHeader} />
        </Heading>
        <div className="text-center text-s">
          <FormattedMessage {...messages.benefitsComingSoon} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.universalBenefitBoxContainer} py5 md-py9 px3 md-px0`}
      data-e2e="universal-benefit-box"
    >
      <Heading
        className={`${styles.heading} text-center text-bold pb5`}
        size="lg"
        tagName="h2"
      >
        <FormattedMessage {...messages.universalBenefitBoxHeader} />
      </Heading>

      <div
        className={`${styles.cardsContainer} flex flex-column overflow-hidden borderRadius-md md-overflow-visible px0 md-px3`}
      >
        {cardContent.map((card) => (
          <Card
            containerClasses={`p4 ${styles.card} borderRadius-md`}
            data-e2e="universal-benefit-card"
            disableBoxShadow
            key={card.id}
            role="article"
          >
            <div
              className={`${styles.cardContent} flex flex-column items-center text-center`}
            >
              <ImageLoader
                alt=""
                className={styles.cardImage}
                includeFallback={{
                  enabled: true,
                  fallbackClassName: 'bg-white',
                }}
                lazyLoad={{ enabled: false }}
                src={`${imageUrl}/universal-benefit-box/${card.image}`}
                wrapInFlex={{
                  enabled: true,
                  constrainWidth: false,
                  flexEmbedWrapperClassName: `${styles.cardImageWrapper} flex-shrink-none flex items-center justify-center`,
                }}
              />
              <div
                className={`${styles.cardText} flex-auto flex flex-column text-center items-center`}
              >
                <div
                  className={`${styles.cardHeading} text-sm text-bold color-greenOnPrimary`}
                >
                  {card.heading}
                </div>
                <div
                  className={`${styles.cardBody} text-xxs lineHeight-normal color-textBodyOnPrimary`}
                >
                  {card.body}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default UniversalBenefitBox;
